import { useId, useState } from 'react';
import type { GraphQLVolunteerSlot } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { formatEventTime } from '@sola/utils/date.ts';
import type { VolunteerActionDialogProps } from './volunteer-action-dialog.tsx';
type VolunteerShift = Pick<GraphQLVolunteerSlot, 'endDate' | 'shiftName' | 'startDate'>;
const getUniqueContactIds = (slots: GraphQLVolunteerSlot[]) => [...new Set(slots.map(({
  contactId
}) => contactId))];
const getUniqueShifts = (slots: GraphQLVolunteerSlot[]) => {
  const shifts = new Map<string, VolunteerShift>();
  for (const {
    endDate,
    shiftName,
    startDate
  } of slots) {
    const key = JSON.stringify([shiftName ?? '', startDate, endDate]);
    shifts.set(key, {
      endDate,
      shiftName,
      startDate
    });
  }
  return [...shifts.values()];
};
export const useVolunteerActionDialog = ({
  action = 'cancel',
  contacts,
  onOpenChange,
  onSubmit,
  open,
  slots
}: Omit<VolunteerActionDialogProps, 'openTrigger'>, language: string) => {
  const [isDialogOpenInternal, setIsDialogOpenInternal] = useState(false);
  const checkboxGroupId = useId();
  const contactIds = getUniqueContactIds(slots);
  const hasMultipleContacts = contactIds.length > 1;
  const translationCount = slots.length > 1 || hasMultipleContacts ? 2 : 1;
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(contactIds);
  const isDialogOpen = open ?? isDialogOpenInternal;
  const isCancelAction = action === 'cancel';
  const [isBusy, setIsBusy] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const selectedSlots = slots.filter(({
    contactId
  }) => selectedContactIds.includes(contactId));
  const shifts = getUniqueShifts(selectedSlots);
  const isSubmitDisabled = isBusy || selectedSlots.length === 0;
  const translationKey = `volunteers:actionDialog.${action}`;
  const titleKey = hasMultipleContacts ? 'selectTitle' : 'title';
  const shiftDetails = shifts.map(({
    endDate,
    shiftName,
    startDate
  }) => [shiftName, formatEventTime(startDate, endDate, language)].filter(Boolean).join(' • ')).filter(Boolean);
  const contactOptions = contactIds.map(contactId_0 => contacts.find(({
    id
  }) => id === contactId_0) ?? {
    email: undefined,
    id: contactId_0,
    name: contactId_0
  });
  const handleOpenChange = (nextOpen: boolean) => {
    setIsDialogOpenInternal(nextOpen);
    setSelectedContactIds(contactIds);
    setShowSubmitError(false);
    onOpenChange?.(nextOpen);
  };
  const handleSubmit = async () => {
    if (isBusy || selectedSlots.length === 0) {
      return;
    }
    setIsBusy(true);
    setShowSubmitError(false);
    try {
      await onSubmit(selectedSlots);
      handleOpenChange(false);
    } catch {
      setShowSubmitError(true);
    } finally {
      setIsBusy(false);
    }
  };
  return {
    checkboxGroupId,
    contactOptions,
    handleOpenChange,
    handleSubmit,
    hasMultipleContacts,
    isBusy,
    isCancelAction,
    isDialogOpen,
    isSubmitDisabled,
    selectedContactIds,
    setSelectedContactIds,
    shiftDetails,
    showSubmitError,
    titleKey,
    translationCount,
    translationKey
  };
};